import React, { useState, useEffect, createContext, useContext } from 'react';
import { auth, db } from '../firebase';
import { onAuthStateChanged, signInWithEmailAndPassword, signOut as firebaseSignOut } from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore'; // Added serverTimestamp

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        const userDocRef = doc(db, 'users', user.uid);
        const userDoc = await getDoc(userDocRef);

        if (userDoc.exists()) {
          // User document exists, load their profile and role
          const userData = { ...user, ...userDoc.data() };
          setCurrentUser(userData);
          setUserRole(userDoc.data().role);
        } else {
          // User document NOT found - This is the new part!
          // Create a new document in Firestore for this user.
          console.log(`User document not found for UID: ${user.uid}. Creating one with default role 'student'.`);
          const newUserProfile = {
            email: user.email, // Get email from the authenticated user object
            uid: user.uid,     // Get UID from the authenticated user object
            role: 'student',   // Assign a default role
            createdAt: serverTimestamp() // Good practice to timestamp creation
          };
          try {
            await setDoc(userDocRef, newUserProfile); // Create the document
            setCurrentUser({ ...user, ...newUserProfile }); // Set current user with this new profile
            setUserRole(newUserProfile.role); // Set the role
            console.log(`Successfully created Firestore document for UID: ${user.uid}`);
          } catch (error) {
            console.error("Error creating user document in Firestore: ", error);
            // Fallback if document creation fails for some reason
            setCurrentUser(user);
            setUserRole(null); // Or some default guest role
          }
        }
      } else {
        // No user logged in
        setCurrentUser(null);
        setUserRole(null);
      }
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  const login = async (email, password) => {
    return signInWithEmailAndPassword(auth, email, password);
  };

  const logout = async () => {
    return firebaseSignOut(auth);
  };

  const updateUserRole = async (uid, role) => {
    // Corrected: Added parentheses for clarity around || and &&
    if (currentUser && (currentUser.role === 'owner' || currentUser.role === 'Admin')) {
      const userDocRef = doc(db, 'users', uid);
      try {
        await setDoc(userDocRef, { role: role }, { merge: true });
        console.log(`Role for user ${uid} updated to ${role}`);
        // If updating current user's role, update state
        if (currentUser && currentUser.uid === uid) {
          setCurrentUser(prevUser => ({ ...prevUser, role }));
          setUserRole(role);
        }
      } catch (error) {
        console.error("Error updating user role: ", error);
        throw error;
      }
    } else {
      console.error("Permission denied: Only owners or admins can update user roles.");
      throw new Error("Permission denied");
    }
  };

  const value = {
    currentUser,
    userRole,
    login,
    logout,
    updateUserRole,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  return useContext(AuthContext);
};
